import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IframeDialogComponent, SubpageHeaderComponent, TrackByItem } from '@components';
import { HomeSectionLabel } from '@constants';
import { catchError, firstValueFrom, map, of } from 'rxjs';
import { RecentlyViewedItem } from 'src/app/search-results/headless-search/services/recently-viewed-item.interface';
import { RecentlyViewedService } from 'src/app/search-results/headless-search/services/recently-viewed.service';

@Component({
    selector: 'app-home-recently-viewed',
    imports: [CommonModule, SubpageHeaderComponent, MatDialogModule, MatIconModule, MatTooltipModule],
    templateUrl: './home-recently-viewed.component.html',
    styleUrls: ['./home-recently-viewed.component.scss'],
})
export class HomeRecentlyViewedComponent implements OnInit, TrackByItem<RecentlyViewedItem> {
    private dialog = inject(MatDialog);
    private bo = inject(BreakpointObserver);
    private recentlyViewedService = inject(RecentlyViewedService);

    /** h4 text for this section */
    title = HomeSectionLabel.RECENTLY_VIEWED;
    recentlyViewedItems: RecentlyViewedItem[] = [];

    /**
     * Load recently viewed items
     */
    ngOnInit(): void {
        this.loadRecentlyViewedItems();
    }

    /**
     * Load Recently Viewwed Items
     */
    private loadRecentlyViewedItems(): void {
        this.recentlyViewedItems = this.recentlyViewedService.recentViews;
    }

    /**
     * Handle view click and navigate to the item URL in a new tab
     * @param title string - The title of the viewed item
     */
    public onViewClick(title: string): void {
        // Find the item with the matching title
        const item = this.recentlyViewedItems.find((item) => item.title === title);
        if (item && item.url) {
            // Open URL in a new tab
            window.open(item.url, '_blank');
        }
    }

    /*
     * Open Modal Dialog with Headless Search Result data
     * @param link - Headless Search Result
     */
    handleOpenDialog(title: string): void {
        const recentlyViewedItem = this.recentlyViewedItems.find((item: RecentlyViewedItem) => item.title === title);
        // MOTE: Open Service Now Support cases as new tab. Cannot open to new modal due to
        // Service Now blocking their site from being iframed with CORs. Will have to revisit post MVP
        // NOTE: Open any Salesforce, Service Now links into new tab. Cannot be iframed in modals due to CORs policy
        // ex. https://genesys.my.site.com/customercare/
        // ex. https://genesyspartner.force.com/
        // ex. https://globalservicesportal.genesys.com
        if (
            recentlyViewedItem.url.indexOf('my.site.com') >= 0 ||
            recentlyViewedItem.url.indexOf('force.com') >= 0 ||
            recentlyViewedItem.url.indexOf('globalservicesportal') >= 0 ||
            recentlyViewedItem?.isSupportCase
        ) {
            // Open in new tab for these URLs that can't be iframed
            window.open(recentlyViewedItem?.url, '_blank');
        } else {
            firstValueFrom(
                this.bo.observe('(max-width: 1020px)').pipe(
                    map((state) => state.matches),
                    catchError((error) => {
                        console.error('Error determining screen size:', error);
                        // Return a default value in case of error
                        return of(false);
                    }),
                ),
            )
                .then((isMobile) => {
                    console.debug('open facet', recentlyViewedItem);
                    this.dialog.open(IframeDialogComponent, {
                        width: isMobile ? '' : '80%',
                        maxWidth: '1400px',
                        height: isMobile ? '90%' : '80%',
                        autoFocus: false,
                        data: {
                            title: recentlyViewedItem?.title,
                            src: recentlyViewedItem?.url || '',
                            dataAnalytics: [this.getAnalyticsData(recentlyViewedItem?.title)],
                        },
                    });
                })
                .catch((error) => {
                    console.error('Failed to open dialog:', error);
                    // Fallback behavior - perhaps open a simpler dialog or navigate directly
                    window.open(recentlyViewedItem?.url, '_blank');
                });
        }
    }

    /**
     * Generate analytics data attribute for recently viewed items
     * @param title - The title of the recently viewed item
     * @returns formatted analytics string
     */
    getAnalyticsData(title: string): string {
        if (title) {
            const trimmedTitle = title.trim();
            if (trimmedTitle.length > 0) {
                return 'recently-viewed-' + trimmedTitle.replace(/\s+/g, '-').toLowerCase();
            }
        }
        return 'recently-viewed-';
    }

    /*
     * Track by Item
     * @param _index - item index
     * @param item - RecentlyViewedItem
     * @returns number or string unique identifier
     */
    trackByItem(index: number, _data: RecentlyViewedItem): NonNullable<number | string> {
        return _data.title;
    }
}
